import * as Sentry from '@sentry/react'
import { createListenerMiddleware } from '@reduxjs/toolkit'

import { getMatterArtifactVersion } from '@/matter-artifacts/fetch/get-matter-artifact-version'
import { MatterArtifactVersionStatus, type MatterArtifactVersionRead } from '@/matter-artifacts/schemas'
import { MatterArtifactsActions } from '@/matter-artifacts/store/slice'
import type { AppDispatch, RootState } from '@/store/store'
import { ApiError } from '@/util/errors/api-error'

export function createMatterArtifactListenerMiddleware() {
  const matterArtifactListenerMiddleware = createListenerMiddleware()
  const startListener = matterArtifactListenerMiddleware.startListening.withTypes<RootState, AppDispatch>()

  const activeMatterArtifactVersionPolls = new Set<string>()

  startListener({
    actionCreator: MatterArtifactsActions.startMatterArtifactVersionStatusPoll,
    effect: async (action, listenerApi) => {
      const { matterArtifactVersionId } = action.payload
      const lifecycleGeneration = listenerApi.getState().matterArtifactsState.lifecycleGeneration
      const pollKey = `${lifecycleGeneration}:${matterArtifactVersionId}`
      if (activeMatterArtifactVersionPolls.has(pollKey)) return

      activeMatterArtifactVersionPolls.add(pollKey)
      const startedAt = Date.now()
      const maxPollingTime = 10 * 60 * 1000

      try {
        while (Date.now() - startedAt <= maxPollingTime) {
          if (listenerApi.getState().matterArtifactsState.lifecycleGeneration !== lifecycleGeneration) return

          let version: MatterArtifactVersionRead
          try {
            version = await getMatterArtifactVersion(matterArtifactVersionId)
          } catch (error) {
            if (listenerApi.getState().matterArtifactsState.lifecycleGeneration !== lifecycleGeneration) return
            if (error instanceof ApiError && error.status >= 400 && error.status < 500 && error.status !== 408 && error.status !== 429) {
              return
            }

            const lifecycleChanged = await listenerApi.condition(
              (_action, state) => state.matterArtifactsState.lifecycleGeneration !== lifecycleGeneration,
              15000
            )
            if (lifecycleChanged) return
            continue
          }

          if (listenerApi.getState().matterArtifactsState.lifecycleGeneration !== lifecycleGeneration) return
          listenerApi.dispatch(MatterArtifactsActions.upsertMatterArtifactVersion(version))
          if (version.status !== MatterArtifactVersionStatus.GENERATING) return
          const lifecycleChanged = await listenerApi.condition((_action, state) => state.matterArtifactsState.lifecycleGeneration !== lifecycleGeneration, 5000)
          if (lifecycleChanged) return
        }

        Sentry.captureException(new Error('Max polling time reached for Matter artifact version'), {
          extra: { matterArtifactVersionId, pollingMinutes: maxPollingTime / 60000 },
        })
      } finally {
        activeMatterArtifactVersionPolls.delete(pollKey)
      }
    },
  })

  return matterArtifactListenerMiddleware
}

export const matterArtifactListenerMiddleware = createMatterArtifactListenerMiddleware()
